import type { AbstractControl, FormGroup } from '@angular/forms';
import {
  ADDRESS_LINE_MAX_LENGTH,
  CITY_MAX_LENGTH,
  COMPANY_OR_FULL_NAME_MAX_LENGTH,
  CONTACT_NAME_MAX_LENGTH,
  EMAIL_MAX_LENGTH,
  SUITE_APT_UNIT_MAX_LENGTH,
  ADDRESS_VALIDATION_ERROR_KEYS,
} from '../validators/address-field.validators';
import { POSTAL_CODE_MAX_LENGTH } from '../validators/postal-code.validators';
import { ENTRY_BUZZER_CODE_MAX_LENGTH } from '../validators/entry-buzzer-code.validators';
import { PHONE_EXTENSION_MAX_LENGTH } from '../validators/phone-extension.validators';
import { applyRuralRoutePoBoxValidation } from './apply-rural-route-po-box-validation';

export type AddressValidatedFieldKey =
  | 'companyOrFullName'
  | 'attentionTo'
  | 'streetAddress'
  | 'suiteAptUnit'
  | 'entryBuzzerCode'
  | 'addressLine2'
  | 'addressLine3'
  | 'city'
  | 'province'
  | 'postalCode'
  | 'email'
  | 'phoneNumber'
  | 'phoneExtension';

/** i18n keys for field labels used in summary error messages. */
export const ADDRESS_FIELD_LABEL_KEYS: Record<AddressValidatedFieldKey, string> = {
  companyOrFullName: 'createShipment.fields.companyOrFullName',
  attentionTo: 'createShipment.fields.attentionTo',
  streetAddress: 'createShipment.fields.streetAddress',
  suiteAptUnit: 'createShipment.fields.suiteAptUnit',
  entryBuzzerCode: 'createShipment.fields.entryBuzzerCode',
  addressLine2: 'createShipment.fields.addressLine2',
  addressLine3: 'createShipment.fields.addressLine3',
  city: 'createShipment.fields.city',
  province: 'createShipment.fields.province',
  postalCode: 'createShipment.fields.postalCode',
  email: 'createShipment.fields.email',
  phoneNumber: 'createShipment.fields.phoneNumber',
  phoneExtension: 'createShipment.fields.phoneExtension',
};

const SUMMARY_FIELD_ORDER: AddressValidatedFieldKey[] = [
  'companyOrFullName',
  'attentionTo',
  'streetAddress',
  'suiteAptUnit',
  'entryBuzzerCode',
  'addressLine2',
  'addressLine3',
  'city',
  'province',
  'postalCode',
  'email',
  'phoneNumber',
  'phoneExtension',
];

/** Fields validated on the return address subsection. */
export const RETURN_ADDRESS_FIELD_ORDER: AddressValidatedFieldKey[] = [
  'streetAddress',
  'suiteAptUnit',
  'entryBuzzerCode',
  'addressLine2',
  'addressLine3',
  'city',
  'province',
  'postalCode',
  'phoneNumber',
  'phoneExtension',
];

/** One summary-banner item with a bold field label and trailing detail text. */
export interface AddressSummaryErrorItem {
  emphasis: string;
  detail: string;
}

export interface AddressSectionErrorViewModel {
  summaryMessages: AddressSummaryErrorItem[];
}

type TranslateFn = (key: string, params?: Record<string, string>) => string;

/** Section keys used to populate address-role placeholders in form-level messages. */
export type AddressErrorSectionKey = 'shipFrom' | 'shipTo' | 'returnAddress';

const ADDRESS_ROLE_LABELS: Record<AddressErrorSectionKey, string> = {
  shipFrom: 'Ship From',
  shipTo: 'Ship To',
  returnAddress: 'Return',
};

type MaxLengthFieldKey =
  | 'companyOrFullName'
  | 'attentionTo'
  | 'streetAddress'
  | 'suiteAptUnit'
  | 'entryBuzzerCode'
  | 'addressLine2'
  | 'addressLine3'
  | 'city'
  | 'postalCode'
  | 'email'
  | 'phoneExtension';

/**
 * Returns the configured maxlength for validated text fields.
 */
function getConfiguredMaxLengthForField(field: MaxLengthFieldKey): number {
  if (field === 'city') {
    return CITY_MAX_LENGTH;
  }

  if (field === 'postalCode') {
    return POSTAL_CODE_MAX_LENGTH;
  }

  if (field === 'entryBuzzerCode') {
    return ENTRY_BUZZER_CODE_MAX_LENGTH;
  }

  if (field === 'phoneExtension') {
    return PHONE_EXTENSION_MAX_LENGTH;
  }

  if (field === 'email') {
    return EMAIL_MAX_LENGTH;
  }

  if (field === 'companyOrFullName') {
    return COMPANY_OR_FULL_NAME_MAX_LENGTH;
  }

  if (field === 'attentionTo') {
    return CONTACT_NAME_MAX_LENGTH;
  }

  if (field === 'suiteAptUnit') {
    return SUITE_APT_UNIT_MAX_LENGTH;
  }

  return ADDRESS_LINE_MAX_LENGTH;
}

/**
 * Reads the maxlength requiredLength from a control error, falling back to the field config.
 */
function resolveMaxLengthLimit(control: AbstractControl, field: MaxLengthFieldKey): number {
  const maxlengthError = control.getError('maxlength') as { requiredLength?: number } | undefined;

  return maxlengthError?.requiredLength ?? getConfiguredMaxLengthForField(field);
}

/**
 * Returns the inline maxlength message for address text fields.
 */
function getMaxLengthFieldErrorMessage(
  field: MaxLengthFieldKey,
  translate: TranslateFn,
  maxLength: number,
): string {
  return translate(
    FIELD_MAX_LENGTH_MESSAGE_KEYS[field] ?? 'createShipment.validation.fieldMaxLength',
    {
      maxLength: String(maxLength),
    },
  );
}

/**
 * Returns the summary maxlength detail for address text fields.
 */
function getMaxLengthSummaryDetail(translate: TranslateFn, maxLength: number): string {
  return translate('createShipment.validation.summaryMaxLengthDetail', {
    maxLength: String(maxLength),
  });
}

function isMaxLengthField(field: AddressValidatedFieldKey): field is MaxLengthFieldKey {
  return (
    field === 'companyOrFullName' ||
    field === 'attentionTo' ||
    field === 'streetAddress' ||
    field === 'suiteAptUnit' ||
    field === 'entryBuzzerCode' ||
    field === 'addressLine2' ||
    field === 'addressLine3' ||
    field === 'city' ||
    field === 'postalCode' ||
    field === 'email' ||
    field === 'phoneExtension'
  );
}

const FIELD_REQUIRED_MESSAGE_KEYS: Partial<Record<AddressValidatedFieldKey, string>> = {
  companyOrFullName: 'createShipment.validation.companyOrFullNameRequiredField',
  streetAddress: 'createShipment.validation.streetAddressRequiredField',
  city: 'createShipment.validation.cityRequiredField',
  province: 'createShipment.validation.provinceRequiredField',
  postalCode: 'createShipment.validation.postalCodeRequiredField',
  email: 'createShipment.validation.emailRequiredField',
  phoneNumber: 'createShipment.validation.phoneNumberRequiredField',
};

const FIELD_REQUIRED_SUMMARY_KEYS: Partial<Record<AddressValidatedFieldKey, string>> = {
  companyOrFullName: 'createShipment.validation.companyOrFullNameRequiredSummaryDetail',
  streetAddress: 'createShipment.validation.streetAddressRequiredSummaryDetail',
  city: 'createShipment.validation.cityRequiredSummaryDetail',
  province: 'createShipment.validation.provinceRequiredSummaryDetail',
  postalCode: 'createShipment.validation.postalCodeRequiredSummaryDetail',
  email: 'createShipment.validation.emailRequiredSummaryDetail',
  phoneNumber: 'createShipment.validation.phoneNumberRequiredSummaryDetail',
};

const FIELD_NAME_RESTRICTED_CHARACTERS_MESSAGE_KEYS: Partial<
  Record<AddressValidatedFieldKey, string>
> = {
  attentionTo: 'createShipment.validation.attentionToRestrictedCharactersField',
  companyOrFullName: 'createShipment.validation.companyOrFullNameRestrictedCharactersField',
};

const FIELD_NAME_RESTRICTED_CHARACTERS_SUMMARY_KEYS: Partial<
  Record<AddressValidatedFieldKey, string>
> = {
  attentionTo: 'createShipment.validation.attentionToRestrictedCharactersSummaryDetail',
  companyOrFullName: 'createShipment.validation.companyOrFullNameRestrictedCharactersSummaryDetail',
};

const FIELD_RESTRICTED_CHARACTERS_MESSAGE_KEYS: Partial<Record<AddressValidatedFieldKey, string>> =
  {
    streetAddress: 'createShipment.validation.streetAddressRestrictedCharactersField',
    addressLine2: 'createShipment.validation.addressLine2RestrictedCharactersField',
    addressLine3: 'createShipment.validation.addressLine3RestrictedCharactersField',
    city: 'createShipment.validation.cityRestrictedCharactersField',
  };

const FIELD_RESTRICTED_CHARACTERS_SUMMARY_KEYS: Partial<Record<AddressValidatedFieldKey, string>> =
  {
    streetAddress: 'createShipment.validation.streetAddressRestrictedCharactersSummaryDetail',
    addressLine2: 'createShipment.validation.addressLine2RestrictedCharactersSummaryDetail',
    addressLine3: 'createShipment.validation.addressLine3RestrictedCharactersSummaryDetail',
    city: 'createShipment.validation.cityRestrictedCharactersSummaryDetail',
  };

const FIELD_MAX_LENGTH_MESSAGE_KEYS: Partial<Record<MaxLengthFieldKey, string>> = {
  attentionTo: 'createShipment.validation.attentionToMaxLengthField',
  companyOrFullName: 'createShipment.validation.companyOrFullNameMaxLengthField',
  streetAddress: 'createShipment.validation.streetAddressMaxLengthField',
  suiteAptUnit: 'createShipment.validation.suiteAptUnitMaxLengthField',
  addressLine2: 'createShipment.validation.addressLine2MaxLengthField',
  addressLine3: 'createShipment.validation.addressLine3MaxLengthField',
  entryBuzzerCode: 'createShipment.validation.entryBuzzerCodeMaxLengthField',
  city: 'createShipment.validation.cityMaxLengthField',
  postalCode: 'createShipment.validation.postalCodeMaxLengthField',
  email: 'createShipment.validation.emailMaxLengthField',
  phoneExtension: 'createShipment.validation.phoneExtensionMaxLengthField',
};

/**
 * Resolves the inline required-field message for a validated address control.
 */
function getRequiredFieldErrorMessage(
  field: AddressValidatedFieldKey,
  translate: TranslateFn,
): string {
  return translate(FIELD_REQUIRED_MESSAGE_KEYS[field] ?? 'createShipment.validation.fieldRequired');
}

/**
 * Resolves the summary required-field detail for a validated address control.
 */
function getRequiredSummaryDetail(field: AddressValidatedFieldKey, translate: TranslateFn): string {
  return translate(
    FIELD_REQUIRED_SUMMARY_KEYS[field] ?? 'createShipment.validation.summaryRequiredDetail',
  );
}

interface InlineFieldErrorResolver {
  applies: (control: AbstractControl, field: AddressValidatedFieldKey) => boolean;
  resolve: (
    control: AbstractControl,
    field: AddressValidatedFieldKey,
    translate: TranslateFn,
  ) => string;
}

interface SummaryFieldErrorResolver {
  applies: (control: AbstractControl, field: AddressValidatedFieldKey) => boolean;
  resolve: (
    control: AbstractControl,
    field: AddressValidatedFieldKey,
    translate: TranslateFn,
  ) => AddressSummaryErrorItem;
}

const INLINE_FIELD_ERROR_RESOLVERS: InlineFieldErrorResolver[] = [
  // Rural/PO Box phone requirement wins over the generic required error (OM-693).
  {
    applies: (control, field) =>
      field === 'phoneNumber' &&
      control.hasError(ADDRESS_VALIDATION_ERROR_KEYS.phoneRequiredPostalKeyword),
    resolve: (_control, _field, translate) =>
      translate('createShipment.validation.ruralRouteFieldRequired'),
  },
  {
    applies: (control) => control.hasError(ADDRESS_VALIDATION_ERROR_KEYS.required),
    resolve: (_control, field, translate) => getRequiredFieldErrorMessage(field, translate),
  },
  {
    applies: (control, field) =>
      field === 'email' && control.hasError(ADDRESS_VALIDATION_ERROR_KEYS.emailInvalid),
    resolve: (_control, _field, translate) =>
      translate('createShipment.validation.emailInvalidField'),
  },
  {
    applies: (control, field) => control.hasError('maxlength') && isMaxLengthField(field),
    resolve: (control, field, translate) =>
      getMaxLengthFieldErrorMessage(
        field as MaxLengthFieldKey,
        translate,
        resolveMaxLengthLimit(control, field as MaxLengthFieldKey),
      ),
  },
  {
    applies: (control) => control.hasError(ADDRESS_VALIDATION_ERROR_KEYS.nameRestrictedCharacters),
    resolve: (_control, field, translate) =>
      translate(
        FIELD_NAME_RESTRICTED_CHARACTERS_MESSAGE_KEYS[field] ??
          'createShipment.validation.nameRestrictedCharactersField',
        { fieldName: translate(ADDRESS_FIELD_LABEL_KEYS[field]) },
      ),
  },
  {
    applies: (control) => control.hasError(ADDRESS_VALIDATION_ERROR_KEYS.restrictedCharacters),
    resolve: (_control, field, translate) =>
      translate(
        FIELD_RESTRICTED_CHARACTERS_MESSAGE_KEYS[field] ??
          'createShipment.validation.restrictedCharactersField',
        { fieldName: translate(ADDRESS_FIELD_LABEL_KEYS[field]) },
      ),
  },
  {
    applies: (control, field) =>
      field === 'entryBuzzerCode' &&
      control.hasError(ADDRESS_VALIDATION_ERROR_KEYS.entryBuzzerCodeInvalid),
    resolve: (_control, _field, translate) =>
      translate('createShipment.validation.entryBuzzerCodeInvalidField'),
  },
  {
    applies: (control, field) =>
      field === 'postalCode' && control.hasError(ADDRESS_VALIDATION_ERROR_KEYS.postalCodeInvalid),
    resolve: (_control, _field, translate) =>
      translate('createShipment.validation.postalCodeInvalidField'),
  },
  {
    applies: (control, field) =>
      field === 'phoneNumber' && control.hasError(ADDRESS_VALIDATION_ERROR_KEYS.phoneNonNumeric),
    resolve: (_control, _field, translate) =>
      translate('createShipment.validation.phoneNonNumericField'),
  },
  {
    applies: (control, field) =>
      field === 'phoneNumber' && control.hasError(ADDRESS_VALIDATION_ERROR_KEYS.phoneInvalid),
    resolve: (_control, _field, translate) =>
      translate('createShipment.validation.phoneInvalidField'),
  },
  {
    applies: (control, field) =>
      field === 'phoneExtension' &&
      control.hasError(ADDRESS_VALIDATION_ERROR_KEYS.phoneExtensionNonNumeric),
    resolve: (_control, _field, translate) =>
      translate('createShipment.validation.phoneExtensionNonNumericField'),
  },
  {
    applies: (control, field) =>
      field === 'phoneExtension' &&
      control.hasError(ADDRESS_VALIDATION_ERROR_KEYS.phoneExtensionRequiresPhone),
    resolve: (_control, _field, translate) =>
      translate('createShipment.validation.phoneExtensionRequiresPhoneField'),
  },
  {
    applies: (control, field) =>
      field === 'addressLine2' &&
      control.hasError(ADDRESS_VALIDATION_ERROR_KEYS.civicStreetRequired),
    resolve: (_control, _field, translate) =>
      translate('createShipment.validation.ruralRouteFieldRequired'),
  },
  {
    applies: (control, field) =>
      field === 'suiteAptUnit' &&
      control.hasError(ADDRESS_VALIDATION_ERROR_KEYS.civicStreetRequired),
    resolve: (_control, _field, translate) =>
      translate('createShipment.validation.ruralRouteFieldRequired'),
  },
  {
    applies: (control, field) =>
      field === 'addressLine2' &&
      control.hasError(ADDRESS_VALIDATION_ERROR_KEYS.civicAddressRequired),
    resolve: (_control, _field, translate) =>
      translate('createShipment.validation.ruralRouteFieldRequired'),
  },
];

const SUMMARY_FIELD_ERROR_RESOLVERS: SummaryFieldErrorResolver[] = [
  // Rural/PO Box phone requirement wins over the generic required error (OM-693).
  {
    applies: (control, field) =>
      field === 'phoneNumber' &&
      control.hasError(ADDRESS_VALIDATION_ERROR_KEYS.phoneRequiredPostalKeyword),
    resolve: (_control, _field, translate) => ({
      emphasis: translate(ADDRESS_FIELD_LABEL_KEYS.phoneNumber),
      detail: translate('createShipment.validation.ruralRouteFieldRequiredSummaryDetail'),
    }),
  },
  {
    applies: (control) => control.hasError(ADDRESS_VALIDATION_ERROR_KEYS.required),
    resolve: (_control, field, translate) => ({
      emphasis: translate(ADDRESS_FIELD_LABEL_KEYS[field]),
      detail: getRequiredSummaryDetail(field, translate),
    }),
  },
  {
    applies: (control, field) =>
      field === 'email' && control.hasError(ADDRESS_VALIDATION_ERROR_KEYS.emailInvalid),
    resolve: (_control, _field, translate) => ({
      emphasis: '',
      detail: translate('createShipment.validation.emailInvalidSummaryDetail'),
    }),
  },
  {
    applies: (control, field) => control.hasError('maxlength') && isMaxLengthField(field),
    resolve: (control, field, translate) => ({
      emphasis: translate(ADDRESS_FIELD_LABEL_KEYS[field]),
      detail: getMaxLengthSummaryDetail(
        translate,
        resolveMaxLengthLimit(control, field as MaxLengthFieldKey),
      ),
    }),
  },
  {
    applies: (control) => control.hasError(ADDRESS_VALIDATION_ERROR_KEYS.nameRestrictedCharacters),
    resolve: (_control, field, translate) => {
      const fieldName = translate(ADDRESS_FIELD_LABEL_KEYS[field]);
      return {
        emphasis: '',
        detail: translate(
          FIELD_NAME_RESTRICTED_CHARACTERS_SUMMARY_KEYS[field] ??
            'createShipment.validation.nameRestrictedCharactersSummaryDetail',
          { fieldName },
        ),
      };
    },
  },
  {
    applies: (control) => control.hasError(ADDRESS_VALIDATION_ERROR_KEYS.restrictedCharacters),
    resolve: (_control, field, translate) => {
      const fieldName = translate(ADDRESS_FIELD_LABEL_KEYS[field]);
      return {
        emphasis: '',
        detail: translate(
          FIELD_RESTRICTED_CHARACTERS_SUMMARY_KEYS[field] ??
            'createShipment.validation.restrictedCharactersSummaryDetail',
          { fieldName },
        ),
      };
    },
  },
  {
    applies: (control, field) =>
      field === 'entryBuzzerCode' &&
      control.hasError(ADDRESS_VALIDATION_ERROR_KEYS.entryBuzzerCodeInvalid),
    resolve: (_control, _field, translate) => ({
      emphasis: translate(ADDRESS_FIELD_LABEL_KEYS.entryBuzzerCode),
      detail: translate('createShipment.validation.entryBuzzerCodeInvalidSummaryDetail'),
    }),
  },
  {
    applies: (control, field) =>
      field === 'postalCode' && control.hasError(ADDRESS_VALIDATION_ERROR_KEYS.postalCodeInvalid),
    resolve: (_control, _field, translate) => ({
      emphasis: translate(ADDRESS_FIELD_LABEL_KEYS.postalCode),
      detail: translate('createShipment.validation.postalCodeInvalidSummaryDetail'),
    }),
  },
  {
    applies: (control, field) =>
      field === 'phoneNumber' && control.hasError(ADDRESS_VALIDATION_ERROR_KEYS.phoneNonNumeric),
    resolve: (_control, _field, translate) => ({
      emphasis: translate(ADDRESS_FIELD_LABEL_KEYS.phoneNumber),
      detail: translate('createShipment.validation.phoneNonNumericSummaryDetail'),
    }),
  },
  {
    applies: (control, field) =>
      field === 'phoneNumber' && control.hasError(ADDRESS_VALIDATION_ERROR_KEYS.phoneInvalid),
    resolve: (_control, _field, translate) => ({
      emphasis: translate(ADDRESS_FIELD_LABEL_KEYS.phoneNumber),
      detail: translate('createShipment.validation.phoneInvalidSummaryDetail'),
    }),
  },
  {
    applies: (control, field) =>
      field === 'phoneExtension' &&
      control.hasError(ADDRESS_VALIDATION_ERROR_KEYS.phoneExtensionNonNumeric),
    resolve: (_control, _field, translate) => ({
      emphasis: '',
      detail: translate('createShipment.validation.phoneExtensionNonNumericSummaryDetail'),
    }),
  },
  {
    applies: (control, field) =>
      field === 'phoneExtension' &&
      control.hasError(ADDRESS_VALIDATION_ERROR_KEYS.phoneExtensionRequiresPhone),
    resolve: (_control, _field, translate) => ({
      emphasis: '',
      detail: translate('createShipment.validation.phoneExtensionRequiresPhoneSummaryDetail'),
    }),
  },
  {
    applies: (control, field) =>
      field === 'addressLine2' &&
      control.hasError(ADDRESS_VALIDATION_ERROR_KEYS.civicAddressRequired),
    resolve: (_control, _field, translate) => ({
      emphasis: translate(ADDRESS_FIELD_LABEL_KEYS.addressLine2),
      detail: translate('createShipment.validation.ruralRouteFieldRequiredSummaryDetail'),
    }),
  },
  {
    applies: (control) => control.hasError(ADDRESS_VALIDATION_ERROR_KEYS.civicStreetRequired),
    resolve: (_control, field, translate) => ({
      emphasis: translate(ADDRESS_FIELD_LABEL_KEYS[field]),
      detail: translate('createShipment.validation.ruralRouteFieldRequiredSummaryDetail'),
    }),
  },
];

/**
 * Marks an address field touched after the customer leaves the control (blur).
 * Enables inline UI validation without triggering Save & Continue or AVS.
 */
export function blurAddressField(control: AbstractControl | null): void {
  control?.markAsTouched();
}

/**
 * Returns the inline field error message after submit or after the field is blurred.
 * Backend address-not-found messaging remains submit-gated.
 */
export function getAddressFieldErrorMessage(
  control: AbstractControl | null,
  field: AddressValidatedFieldKey,
  translate: TranslateFn,
  submitted: boolean,
): string | null {
  if (!control) {
    return null;
  }

  if (!submitted && !control.touched) {
    return null;
  }

  if (
    submitted &&
    field === 'streetAddress' &&
    control.parent?.hasError(ADDRESS_VALIDATION_ERROR_KEYS.addressNotFound)
  ) {
    return translate('createShipment.validation.addressNotFoundField');
  }

  if (!control.invalid) {
    return null;
  }

  const match = INLINE_FIELD_ERROR_RESOLVERS.find((resolver) => resolver.applies(control, field));
  return match ? match.resolve(control, field, translate) : null;
}

/**
 * Builds ordered summary messages for the section error banner after submit.
 */
export function collectAddressSectionErrors(
  formGroup: FormGroup,
  translate: TranslateFn,
  submitted: boolean,
  fields: AddressValidatedFieldKey[] = SUMMARY_FIELD_ORDER,
  summaryExtras?: AddressSummaryErrorItem[],
  sectionKey?: AddressErrorSectionKey,
): AddressSectionErrorViewModel | null {
  if (!submitted) {
    return null;
  }

  const summaryMessages: AddressSummaryErrorItem[] = [...(summaryExtras ?? [])];
  const addressRole = sectionKey ? ADDRESS_ROLE_LABELS[sectionKey] : 'Address';

  for (const field of fields) {
    if (
      field === 'streetAddress' &&
      formGroup.hasError(ADDRESS_VALIDATION_ERROR_KEYS.addressNotFound)
    ) {
      summaryMessages.push({
        emphasis: '',
        detail: translate('createShipment.validation.addressNotFoundSummaryDetail', {
          addressRole,
        }),
      });
      continue;
    }

    const control = formGroup.get(field);
    if (!control?.invalid) {
      continue;
    }

    const match = SUMMARY_FIELD_ERROR_RESOLVERS.find((resolver) =>
      resolver.applies(control, field),
    );
    if (match) {
      summaryMessages.push(match.resolve(control, field, translate));
    }
  }

  return summaryMessages.length > 0 ? { summaryMessages } : null;
}

const SERVICEABILITY_SECTION_KEYS = ['shipFrom', 'shipTo', 'returnAddress'] as const;

/**
 * Collects page-level serviceability summary items from sections flagged by the API.
 */
export function collectServiceabilityErrorSummary(
  form: FormGroup,
  translate: TranslateFn,
): AddressSummaryErrorItem[] {
  const summaryMessages: AddressSummaryErrorItem[] = [];

  for (const sectionKey of SERVICEABILITY_SECTION_KEYS) {
    const sectionGroup = form.get(sectionKey) as FormGroup | null;

    if (!sectionGroup?.hasError(ADDRESS_VALIDATION_ERROR_KEYS.addressUnserviceable)) {
      continue;
    }

    summaryMessages.push({
      emphasis: '',
      detail: translate('createShipment.validation.serviceabilityNotServiceableSummaryDetail'),
    });
  }

  return summaryMessages;
}

/**
 * Marks all controls touched before client-side submit validation.
 */
export function validateAddressSectionFormGroup(formGroup: FormGroup): void {
  formGroup.markAllAsTouched();
  formGroup.updateValueAndValidity({ emitEvent: false });
}

/**
 * Validates ship-from, ship-to, and optional return address sections on save.
 */
export function validateShipmentAddressesForm(form: FormGroup): boolean {
  const shipFrom = form.get('shipFrom') as FormGroup | null;
  const shipTo = form.get('shipTo') as FormGroup | null;
  const returnAddress = form.get('returnAddress') as FormGroup | null;
  const useDifferentReturnAddress = form.get('useDifferentReturnAddress')?.value === true;

  if (shipFrom) {
    validateAddressSectionFormGroup(shipFrom);
  }

  if (shipTo) {
    validateAddressSectionFormGroup(shipTo);
  }

  if (useDifferentReturnAddress && returnAddress) {
    validateAddressSectionFormGroup(returnAddress);
  }

  applyRuralRoutePoBoxValidation(form, true);

  form.updateValueAndValidity({ emitEvent: false });

  const shipFromValid = shipFrom?.valid ?? true;
  const shipToValid = shipTo?.valid ?? true;
  const returnAddressValid = !useDifferentReturnAddress || (returnAddress?.valid ?? true);

  return shipFromValid && shipToValid && returnAddressValid;
}
